import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { SurveysService } from '@src/app/shared/backend/clients';
import { SurveyPostSaleRequest } from '@src/app/shared/backend/clients/model/surveyPostSaleRequest';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { FiltersPanelChangeEvent, FiltersPanelFieldOverrides } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { TableSettingService } from '@src/app/shared/services/table.setting.service';
import { ActiveInactivePipe } from '@app/shared/pipes/activeInactive.pipe';
import { FriendlyBooleanPipe } from '@app/shared/pipes/friendlyBoolean.pipe';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';

@Component({
    selector: 'app-surveys',
    templateUrl: './surveys.component.html',
    styleUrl: './surveys.component.scss',
    host: {
        class: 'app-surveys'
    },
    standalone: false
})
export class SurveysComponent extends BaseListComponent implements OnInit, OnDestroy {
  private initialLoadDone = false;
  private readonly componentDestroyed$ = new Subject<void>();

  public displaySurveySettingsModalbox = false;

  public readonly surveysTableName = TableSettingService.SurveysTableName;
  public filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};

  public allowCreateSurvey = false;

  // Dropdown: is anonymous
  // private anonymousOptions: Array<{ id: string | number; text: string }> = [];

  // Dropdown: is published
  private publishedOptions: Array<{ id: string | number; text: string }> = [];

  // Dropdown: status
  private activeOptions: Array<{ id: string | number; text: string }> = [];

  public displayExportModalbox: boolean = false;

  constructor(
    private router: Router,
    public override tableSettingService: TableSettingService,
    private toasterService: ToasterService,
    private surveysService: SurveysService,
    private permissionService: PermissionService,
    private translateService: TranslateService) {
    super();
  }

  ngOnInit() {
    this.init();
  }

   async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    this.allowCreateSurvey = await this.permissionService.hasActionPermissions(Actions.create_surveys);

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        // this.rebuildIsAnonymousOptions  ();
        this.rebuildIsPublishedOptions();
        this.rebuildActiveOptions();
        this.rebuildFiltersPanelOverrides();
        this.rebuildSortPanelColumnLabels()
      });

    await this.tableSettingService.init();
    await super.setCache(this.surveysTableName);

    // i18n propagation on lang change
    // this.rebuildIsAnonymousOptions();
    this.rebuildIsPublishedOptions();
    this.rebuildActiveOptions();

    this.rebuildFiltersPanelOverrides();

    this.initSortPanel({
      defaultSort: { key: 'name', direction: 'DESC' },
      labelResolver: col => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);
        return translated && translated !== translationKey ? translated : col.header;
      }
    });

    this.rebuildSortPanelColumnLabels();

    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });
  }

  //  rebuildIsAnonymousOptions(): void {
  //   const pipe = new FriendlyBooleanPipe(this.translateService);
  //   this.anonymousOptions = pipe.values;
  // }

   rebuildIsPublishedOptions(): void {
    const pipe = new FriendlyBooleanPipe(this.translateService);
    this.publishedOptions = pipe.values;
  }

   rebuildActiveOptions(): void {
    const pipe = new ActiveInactivePipe(this.translateService);
    this.activeOptions = pipe.values;
  }

  override async get(): Promise<void> {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.page = await firstValueFrom(this.surveysService.apiSurveysFilterTablePost(this.filter));
    this.data = this.page.results;
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  protected override onCriteriaResetRequested(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.surveysTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.surveysTableName);

    if (!stored) {
      return;
    }

    this.pageNumber = stored.valuesByFieldKey.pageNumber;
    this.pageSize = stored.valuesByFieldKey.pageSize;
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    const nextFilters = applyFiltersPanelChange(
      evt,
      this.filter.filters as any,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    if (evt.source === 'apply' || evt.source === 'clear') {
      this.pageNumber = 1;
      this.filter.pageNumber = 1;
      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    this.sortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return field.length > 0 && field.toLowerCase() !== 'actions' && col?.sortable !== false;
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        return {
          key: col.field,
          label: translated && translated !== translationKey ? translated : col.header
        };
      });

    const allowedKeys = new Set(this.sortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  private rebuildFiltersPanelOverrides(): void {
    this.filtersPanelFieldOverrides = {
      // isAnonymous: {
      //   fieldLabel: 'TABLE.HEADER.IsAnonymous',
      //   isMultiselect: true,
      //   options: this.anonymousOptions ?? [],
      //   optionLabel: 'text',
      //   optionValue: 'id',
      //   placeholder: 'Select...',
      //   searchMode: 'local',
      //   showSearchbox: false,
      //   showClear: true,
      //   compactUi: true
      // },
      isPublished: {
        fieldLabel: 'TABLE.HEADER.IsPublished',
        isMultiselect: true,
        options: this.publishedOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',
        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
      isActive: {
        fieldLabel: 'TABLE.HEADER.Status',
        isMultiselect: true,
        options: this.activeOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',
        placeholder: 'Select...',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      }
    };
  }

  goToCreateSurveyForm(): void {
    this.router.navigate(['/surveys/add']);
  }

  goToSurveyDetails(event: any, surveyId: string | number): void {
    this.router.navigate([`/surveys/${surveyId}`]);
  }

  onOpenSurveySettingsModalbox() {
    this.displaySurveySettingsModalbox = true;
  }

  async onSurveySettingsSave(data: SurveyPostSaleRequest): Promise<void> {

    console.log('Selected survey:', data);

    try {
      await firstValueFrom(this.surveysService.apiSurveysPostSalePut(data));

      const title = this.translateService.instant('SURVEYS.SURVEY_SETTINGS_SAVE_TITLE_SUCCESS');
      const message = this.translateService.instant('SURVEYS.SURVEY_SETTINGS_SAVE_SUCCESS');
      this.toasterService.show('success', title, message);
      this.displaySurveySettingsModalbox = false;

    } catch (error) {
      const title = this.translateService.instant('SURVEYS.SURVEY_SETTINGS_SAVE_TITLE_FAIL');
      const message = this.translateService.instant('SURVEYS.SURVEY_SETTINGS_SAVE_FAIL');
      this.toasterService.show('error', title, message);

      console.error('Error saving post sale survey', error);
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
